import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Check, Loader2, Phone, Send, Star } from 'lucide-react';
import { useState } from 'react';
import { HelpHint } from './HelpHint';
import { api } from '../lib/api';
import type { Candidate } from '../lib/types';

type Props = {
  jobId: string;
  areaId: string | null;
  areaName: string | null;
  requiredSkills: string[];
  currentAssigneeId?: string | null;
};

const CHANNEL_LABEL: Record<string, string> = {
  whatsapp: 'WhatsApp',
  sms: 'SMS',
  email: 'Email',
};

export function AssignPanel({ jobId, areaId, areaName, requiredSkills, currentAssigneeId }: Props) {
  const queryClient = useQueryClient();
  const [showAll, setShowAll] = useState(false);
  const [selected, setSelected] = useState<Candidate | null>(null);
  const [channel, setChannel] = useState<string>('');
  const [result, setResult] = useState<string | null>(null);

  const candidates = useQuery({
    queryKey: ['candidates', areaId, requiredSkills.join(',')],
    queryFn: () => api.candidates(areaId ?? undefined, requiredSkills),
  });

  const assign = useMutation({
    mutationFn: () => api.assign(jobId, selected!.id, channel || undefined),
    onSuccess: async (data) => {
      setResult(
        data.delivery.ok
          ? `Sent to ${data.technician.name} on ${CHANNEL_LABEL[data.channel]} — ${data.technician.phoneE164}`
          : `Assigned, but delivery failed: ${data.delivery.detail}`,
      );
      setSelected(null);
      await queryClient.invalidateQueries();
    },
  });

  const all = candidates.data?.candidates ?? [];
  const eligible = all.filter((c) => c.blockers.length === 0);
  // The zone filter is a convenience, never a restriction (AREAS.md §5).
  const inZone = eligible.filter((c) => c.inZone);
  const shown = showAll ? all : inZone.length > 0 ? inZone : eligible;

  const effectiveChannel = channel || selected?.channels[0] || 'whatsapp';

  return (
    /*
      `id="assign"` is the target of the Assign link on the tickets list, so a
      click there lands on this panel rather than the top of the ticket. It is
      also what `scroll-mt` is for: without it the browser puts the anchor
      under the sticky header and the panel appears not to be there, which is
      the complaint this whole change answers.
    */
    <section id="assign" className="card scroll-mt-24">
      <div className="mb-1 flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 font-heading text-lg">
          Assign
          <HelpHint>
            Ranked by zone, skills and current load. It is a suggestion — assign anyone you like, and
            the override is recorded.
          </HelpHint>
        </h3>
        {candidates.isFetching && (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" />
        )}
      </div>

      <p className="mb-4 text-sm text-muted-foreground">
        {areaName ? (
          <>
            Area <strong>{areaName}</strong>
            {candidates.data?.zoneCode && <> · zone {candidates.data.zoneCode}</>}
          </>
        ) : (
          'No area set on this ticket — showing everyone.'
        )}
      </p>

      {result && (
        <div
          role="status"
          className="mb-4 flex items-start gap-2 rounded-xl bg-secondary-50 p-3 text-sm text-secondary-700"
        >
          <Check className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{result}</span>
        </div>
      )}

      {assign.isError && (
        <div
          role="alert"
          className="mb-4 flex items-start gap-2 rounded-xl bg-danger-50 p-3 text-sm text-danger-600"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{(assign.error as Error).message}</span>
        </div>
      )}

      <ul className="divide-y divide-border">
        {shown.map((candidate, index) => {
          const isSelected = selected?.id === candidate.id;
          const blocked = candidate.blockers.length > 0;

          return (
            <li key={candidate.id}>
              <button
                type="button"
                onClick={() => {
                  setSelected(isSelected ? null : candidate);
                  setChannel('');
                  setResult(null);
                }}
                className={`flex w-full items-start gap-3 py-3 text-left transition-colors ${
                  isSelected ? 'bg-primary-50/60' : 'hover:bg-muted/40'
                } ${blocked ? 'opacity-60' : ''}`}
              >
                <span
                  className={`mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${
                    isSelected ? 'border-primary-500 bg-primary-500' : 'border-border'
                  }`}
                  aria-hidden="true"
                >
                  {isSelected && <Check className="h-2.5 w-2.5 text-white" />}
                </span>

                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{candidate.name}</span>
                    {index === 0 && !blocked && !showAll && (
                      <span className="badge bg-primary-50 text-primary-700">
                        <Star className="h-3 w-3" aria-hidden="true" />
                        suggested
                      </span>
                    )}
                    {candidate.id === currentAssigneeId && (
                      <span className="badge bg-muted text-muted-foreground">current</span>
                    )}
                    {!candidate.inZone && !blocked && (
                      <span className="badge bg-accent-50 text-accent-600">out of zone</span>
                    )}
                  </span>

                  {/* ROLES.md §4: the dispatcher confirms the number before it goes. */}
                  <span className="mt-0.5 flex items-center gap-1.5 text-sm text-muted-foreground">
                    <Phone className="h-3 w-3" aria-hidden="true" />
                    {candidate.phoneE164}
                    <span className="text-xs">
                      · {candidate.zones.join(', ') || 'no zones'} · {candidate.openJobs}/
                      {candidate.maxConcurrent} jobs
                    </span>
                  </span>

                  <span className="mt-1 block text-xs text-muted-foreground">
                    {blocked ? (
                      <span className="text-danger-600">{candidate.blockers.join(' · ')}</span>
                    ) : (
                      candidate.reasons.join(' · ')
                    )}
                  </span>
                </span>
              </button>

              {isSelected && (
                <div className="space-y-3 pb-4 pl-7">
                  <div>
                    <label htmlFor="channel" className="label">
                      Channel
                      <HelpHint>
                        WhatsApp gives them buttons. SMS and email give them a link that does exactly
                        the same thing.
                      </HelpHint>
                    </label>
                    <select
                      id="channel"
                      value={effectiveChannel}
                      onChange={(e) => setChannel(e.target.value)}
                      className="input-field"
                    >
                      {candidate.channels.map((c) => (
                        <option key={c} value={c}>
                          {CHANNEL_LABEL[c] ?? c} {c === candidate.channels[0] ? '(preferred)' : ''}
                        </option>
                      ))}
                      {!candidate.channels.includes('email') && candidate.email && (
                        <option value="email">Email</option>
                      )}
                    </select>
                  </div>

                  <p className="rounded-xl bg-muted/60 p-3 text-sm">
                    Sending to{' '}
                    <strong>
                      {effectiveChannel === 'email'
                        ? (candidate.email ?? 'no email on file')
                        : candidate.phoneE164}
                    </strong>{' '}
                    on {CHANNEL_LABEL[effectiveChannel] ?? effectiveChannel}.
                  </p>

                  {blocked && (
                    <p className="text-sm text-accent-600">
                      This technician is blocked: {candidate.blockers.join('; ')}. You can still
                      assign them — the override is recorded.
                    </p>
                  )}

                  <button
                    type="button"
                    onClick={() => assign.mutate()}
                    disabled={assign.isPending}
                    className="btn-primary"
                  >
                    {assign.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    ) : (
                      <Send className="h-4 w-4" aria-hidden="true" />
                    )}
                    Assign and send
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {shown.length === 0 && !candidates.isPending && (
        <p className="py-6 text-center text-sm text-muted-foreground">
          Nobody in this zone is free. Use “Show all technicians”.
        </p>
      )}

      <button
        type="button"
        onClick={() => setShowAll((v) => !v)}
        className="mt-3 text-sm font-semibold text-primary-600 hover:underline"
      >
        {showAll ? `Show only ${candidates.data?.zoneCode ?? 'in-zone'}` : 'Show all technicians'}
        <span className="ml-1 font-normal text-muted-foreground">
          ({showAll ? shown.length : all.length} total)
        </span>
      </button>
    </section>
  );
}
